import React from "react";
import { CalendarX2, Pencil, Plus, Send, Trash2 } from "lucide-react";
import type { ITimesheet } from "../../types/timesheet.api.types";
import { CARD_CLASS } from "../../constants/dashboard";
import { projectColor } from "../../constants/timesheet";
import { SimpleTooltip } from "../common";
import {
  computeTaskHours,
  DAY_LABELS,
  formatHours,
  type DraftTask,
} from "../../utils/timesheetWeek";

interface WeekendCardProps {
  day: Date;
  entry?: ITimesheet;
  tasks: DraftTask[];
  mode: "editable" | "locked";
  isSubmitting?: boolean;
  isWeekOff?: boolean;
  dayTargetHours?: number;
  onTasksChange: (tasks: DraftTask[]) => void;
  onSubmitSingleDay?: () => void;
  onAddTask?: () => void;
  onEditTask?: (index: number) => void;
}

const WeekendCard: React.FC<WeekendCardProps> = ({
  day,
  entry,
  tasks,
  mode,
  isSubmitting = false,
  isWeekOff = true,
  dayTargetHours = 0,
  onTasksChange,
  onSubmitSingleDay,
  onAddTask,
  onEditTask,
}) => {
  const editable = mode === "editable";
  const dayHours =
    mode === "locked"
      ? entry?.totalHours ?? 0
      : tasks.reduce((sum, t) => sum + computeTaskHours(t), 0);

  const rows = editable
    ? tasks.map((t) => ({
        key: t.key,
        name: t.name,
        project: t.project,
        hours: computeTaskHours(t),
      }))
    : (entry?.tasks ?? []).map((t, i) => ({
        key: `${entry?.id}-${i}`,
        name: t.name,
        project: t.project,
        hours: t.hours,
      }));

  const removeTask = (index: number) => {
    onTasksChange(tasks.filter((_, i) => i !== index));
  };

  return (
    <div className={`${CARD_CLASS} p-3 sm:p-4 flex flex-col gap-2`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-label font-bold text-text-primary">
            {DAY_LABELS[day.getDay()]}
          </span>
          <span className="text-label font-semibold text-text-tertiary">
            {String(day.getDate()).padStart(2, "0")}
          </span>
          {isWeekOff ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-bold uppercase tracking-wide bg-surface-muted text-text-secondary">
              Week-off
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-caption font-bold uppercase tracking-wide bg-primary-50 text-primary-700 border border-primary-100">
              Working Day
            </span>
          )}
          {dayHours > 0 && (
            <span className="text-body-sm font-bold text-text-strong whitespace-nowrap">
              {formatHours(dayHours)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {editable && entry?.status === "draft" && tasks.length > 0 && onSubmitSingleDay && (
            <SimpleTooltip
              label="Submit this day's timesheet"
              side="bottom"
              className="inline-block"
              tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
            >
              <button
                type="button"
                onClick={onSubmitSingleDay}
                disabled={isSubmitting}
                aria-label={`Submit ${DAY_LABELS[day.getDay()]} timesheet`}
                className="w-8 h-8 flex-none grid place-items-center bg-primary-50 text-primary-700 hover:bg-primary-700 hover:text-white border border-primary-200 rounded-lg transition-all disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </SimpleTooltip>
          )}
          {editable && onAddTask && (
            <button
              type="button"
              onClick={onAddTask}
              disabled={isSubmitting}
              aria-label={`Add task for ${DAY_LABELS[day.getDay()]}`}
              className="w-8 h-8 flex-none grid place-items-center border border-dashed border-[var(--color-border-input)] bg-transparent rounded-lg text-primary-700 hover:bg-primary-700/10 hover:border-primary-700 transition-all disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {rows.map((t, index) => {
            const hours =
              typeof t.hours === "number" && t.hours > 0
                ? t.hours
                : computeTaskHours(t as DraftTask);

            return (
              <div
                key={`${t.name}-${index}`}
                className={`flex items-center gap-2 rounded-lg bg-[var(--color-bg-hover)] px-2 py-1.5 ${
                  editable && onEditTask ? "group cursor-pointer hover:bg-surface-muted transition-colors" : ""
                }`}
                onClick={editable && onEditTask ? () => onEditTask(index) : undefined}
              >
                <span
                  className="w-[3px] flex-none rounded-[2px]"
                  style={{ background: projectColor(t.project) }}
                />
                <span className="flex-1 min-w-0 text-body-sm font-bold text-text-primary whitespace-nowrap overflow-hidden text-ellipsis">
                  {t.name}
                </span>
                <span className="text-caption font-bold text-text-strong whitespace-nowrap">
                  {formatHours(hours)}
                </span>
                {editable && (
                  <>
                    <SimpleTooltip
                      label="Edit task"
                      side="top"
                      tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
                    >
                      <span className="text-[var(--color-layout-header-icon-secondary)] hover:text-primary-700 transition-colors">
                        <Pencil className="w-3 h-3" />
                      </span>
                    </SimpleTooltip>
                    <SimpleTooltip
                      label="Delete task"
                      side="top"
                      tooltipClassName="bg-white text-[var(--color-text-primary)] border border-[var(--color-border-primary)] shadow-xl"
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTask(index);
                        }}
                        aria-label={`Remove task ${index + 1}`}
                        disabled={isSubmitting}
                        className="p-0.5 rounded text-[var(--color-layout-header-icon-secondary)] hover:text-[var(--color-error-dark)] transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </SimpleTooltip>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[var(--color-layout-header-icon-secondary)]">
          <CalendarX2 className="w-4 h-4" />
          <span className="text-caption font-semibold">No overtime logged</span>
        </div>
      )}
    </div>
  );
};

export default WeekendCard;
